<template>
  <div
    :class="cssClasses"
    :style="serviceStyles">
    <div
      v-if="!daysMode && reservationHoursFirst"
      :class="{ 'time': true, 'in-progress': !isBlock && useInProgress && inProgress }"
      :data-lang-in-progress="$t('reservation.inProgress')">
      <icon
        v-if="isBlockedDate"
        name="lock"
        primaryColor="color-text-light"
        size="xxs" />

      <span class="start-time">
        {{ startTime[0] }}<sup>{{ startTime[1] }}</sup>
      </span>
      -
      <span class="end-time">
        {{ endTime[0] }}<sup>{{ endTime[1] }}</sup>
      </span>
    </div>

    <div
      v-if="!daysMode && displayWorker"
      :class="{ 'avatar': true, 'is-blocked': isBlockedDate }">
      <icon
          v-if="isBlockedDate"
          name="lock"
          primaryColor="color-text-light"
          size="s" />
      <avatar
        :worker="reservationData.worker"
        :name="reservationData.worker.name" />
    </div>

    <div class="time-data">
      <div
        v-if="showReservationID"
        class="reservation-id">
        <small>
          {{ $t('reservation.id') }}:
          <a
            href="#"
            @click.prevent="showReservationDetails">
            #{{ reservationData.id }}
          </a>
        </small>
      </div>

      <strong
        v-if="forceShowDate || (!daysMode && !isReservation && !inCalendar) || (!daysMode && isTabbedView)"
        class="date">
        {{ date }}
      </strong>

      <div
        v-if="!daysMode && !reservationHoursFirst"
        :class="{ 'time': true, 'in-progress': !isBlock && useInProgress && inProgress }"
        :data-lang-in-progress="$t('reservation.inProgress')">
        <icon
          v-if="isBlockedDate && !displayWorker"
          name="lock"
          primaryColor="color-text-light"
          size="xxs" />

        <span class="start-time">
          {{ startTime[0] }}<sup>{{ startTime[1] }}</sup>
        </span>
        -
        <span class="end-time">
          {{ endTime[0] }}<sup>{{ endTime[1] }}</sup>
        </span>
      </div>

      <span
        v-if="!daysMode"
        :style="forceShowDate ? 'margin-left: 0;' : ''"
        class="duration">
        (
          <icon
            name="time"
            primaryColor="color-text-light"
            size="xxs" />
          {{ duration }}
        )
      </span>

      <span
        v-else
        class="duration-range">
        <span class="date">
          <strong>{{ $t('day.from') }}</strong>
          {{ reservationDateStart }}
        </span>
        <span class="date">
          <strong>{{ $t('day.to') }}</strong>
          {{ reservationDateEnd }}
        </span>
      </span>

      <periodicity-viewer
        :itemData="reservationData"
        :infoBoxPosition="indexOnList < 2 ? 'bottom' : 'top'"
        :parentSelectorWithOverflow="null"
        :rowMode="!isBlock"
        :contentMode="'simple'"
        :autoRenew="reservationData.periodicity_auto_renew" />

      <div
        v-if="!isReservation && !inCalendar"
        class="occupancy">
        <span class="occupancy-time">
          <template v-if="!occupancyReservations">
            {{ $t('reservation.noPeopleInThisTerm') }}
          </template>
          <template v-if="occupancyReservations">
            {{ $t('reservation.forThisHour') }} <template v-if="serviceName">{{ $t('reservation.andService') }}</template> {{ $t('reservation.youHave') }} {{ occupancyReservations }} {{ reservationWordVariations(occupancyReservations) }}
            <template v-if="peopleNumberEnabled">({{ occupancyPeople }} {{ $t('reservation.peopleCounter') }})</template>
          </template>
        </span>
      </div>
    </div>

    <div :class="{ 'contact-data': true, 'no-worker': daysMode || !displayWorker }">
      <reservation-status
        v-if="showStatus"
        :status="reservationData.status" />

      <strong class="client-name">
        {{ clientName }}
      </strong>

      <badge-first-visit
        v-if="reservationData.is_customer_first_inquiry"
        class="client-first-visit" />

      <span
        v-if="peopleNumberEnabled && peopleNumber"
        class="people-number">
        <icon
          name="profile-big"
          primaryColor="color-text-light"
          size="xs" />
        <span class="people-number-counter">
          {{ peopleNumber }}<template v-if="childrenNumber"> + {{ childrenNumber }}</template>
        </span>
      </span>

      <a
        v-if="phone && userCanSeeContactData"
        href="#"
        :data-tel="phone"
        @click.prevent="$window.location.href='tel:' + phone;"
        class="phone">
        <icon
          name="phone"
          primaryColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.phonePrefix') }} {{ phone }}
        </span>
      </a>

      <a
        v-if="email && userCanSeeContactData"
        href="#"
        :data-email="email"
        :title="email"
        @click.prevent="$window.location.href='mailto:' + email;"
        class="email">
        <icon
          name="mailbox"
          primaryColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.emailPrefix') }} {{ email }}
        </span>
      </a>

      <span
        v-if="reservationData.customer_id && userCanSeeContactData"
        class="client-info"
        @click="$bus.$emit('show-customer-popup', reservationData.customer_id)">
        <icon
          name="info-circle-alt"
          primaryColor="color-text-light"
          strokeColor="color-text-light"
          size="xs" />
        <span class="tooltip">
          {{ $t('common.ui.clientInfo') }}
        </span>
      </span>
    </div>

    <div class="price">
      <span
        v-if="serviceName && servicesEnabled"
        :class="{ 'service': true, 'no-price': !(paymentsEnabled && hasPrice) }">
        {{ serviceName }}

        <template v-if="reservationData.periodicity_name">
          <br>
          <span class="periodicity-name">
            {{ reservationData.periodicity_name }}
          </span>
        </template>

        <tooltip
          v-if="reservationData.service.category"
          :text="$t('reservation.category') + reservationData.service.category" />
      </span>

      <span
        v-if="paymentsEnabled && hasPrice && (userCanSeeFullPaymentsData || userCanSeeLimitedPaymentsData)"
        class="total-price">
        <template v-if="reservationData.has_periodicity && reservationData.periodicity_payment_type === 0">
          {{ $t('common.ui.fullCycleCost') }} {{ totalPrice }}
        </template>
        <template v-else>
          {{ $t('common.ui.cost') }} {{ totalPrice }}
        </template>
      </span>

      <component :is="isBlock ? 'div' : 'span'"
        v-if="paymentsEnabled && hasPrice"
        class="payment-status">
        <template v-if="!isBlock && (userCanSeeFullPaymentsData || userCanSeeLimitedPaymentsData)">- </template>
        <span :class="paymentStatusClass">{{ paymentStatus }}</span>
      </component>
    </div>

    <div
      v-if="showActions"
      class="actions">
      <btn
        v-if="!isBlockedDate && !isReservation && userCanAddReservation"
        :type="acceptInProgress ? 'loader icon' : 'icon'"
        :title="$t('reservation.acceptReservation')"
        @click.native="accept"
        :disabled="acceptInProgress || rejectInProgress || reservationData.status === 3 || blockChangeActions"
        :iconName="!acceptInProgress ? 'ok-circle' : 'loader'"
        iconColor="color-success" />

      <btn
        v-if="!isBlockedDate && isReservation && attendancesListEnabled"
        :type="presentInProgress ? 'loader icon' : 'icon'"
        :title="$t('reservation.clientNotPresent')"
        @click.native.prevent="clientNotPresent()"
        :disabled="presentInProgress"
        :iconName="!presentInProgress ? 'thumbs-down-circle' : 'loader'"
        iconColor="color-warning" />

      <btn
        v-if="showContact && email && userCanSeeContactData"
        :url="'mailto:' + email"
        type="icon"
        :title="$t('reservation.contactWithClient')"
        :disabled="rejectInProgress || acceptInProgress"
        iconName="mailbox-circle"
        iconColor="color-notice" />

      <btn
        v-if="!isBlockedDate && userCanAddReservation"
        :type="rejectInProgress ? 'loader icon' : 'icon'"
        :title="reservationData.status === 3 ? $t('reservation.cancelReservation') : $t('reservation.rejectReservation')"
        @click.native="reject"
        :disabled="acceptInProgress || rejectInProgress || (reservationData.status >= 4 && !waitingListEnabled) || blockChangeActions"
        :iconName="!rejectInProgress ? 'cross-circle' : 'loader'"
        iconColor="color-danger" />

      <btn
        v-if="isBlockedDate && userCanManageBlockedTermin(reservationData)"
        :type="unblockInProgress ? 'loader icon' : 'icon'"
        :title="$t('reservation.unblockDate')"
        @click.native="unblock"
        :disabled="unblockInProgress"
        :iconName="!unblockInProgress ? 'cross-circle' : 'loader'"
        iconColor="color-danger" />

      <btn
        type="icon"
        :title="['blocking', 'cart', 'company'].indexOf(reservationData.type) > -1 ? $t('reservation.moreInfoAboutBlockedDate') : $t('reservation.moreInfoAboutReservation')"
        :disabled="unblockInProgress || rejectInProgress || acceptInProgress"
        iconName="info-circle"
        iconColor="color-text-light"
        @click.native="showReservationDetails" />
    </div>
  </div>
</template>

<script>
import Avatar from './../common/Avatar';
import CalendarUtils from './../helpers/CalendarUtils';
import Configuration from './../mixins/Configuration';
import CurrencyUtils from './../helpers/CurrencyUtils';
import ErrorUtils from './../helpers/ErrorUtils.js';
import PeriodicityViewer from '../common/PeriodicityViewer.vue';
import ReservationStatus from './../common/ReservationStatus';
import ReservationStatusChange from './../mixins/ReservationStatusChange';
import Tooltip from './../common/Tooltip';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';

export default {
  name: 'reservation',
  mixins: [
    Configuration,
    ReservationStatusChange,
    UserTypes,
    WordVariations
  ],
  props: {
    isBlock: {
      type: Boolean,
      default: true
    },
    isReservation: {
      type: Boolean,
      default: false
    },
    inCalendar: {
      type: Boolean,
      default: false
    },
    showReservationID: {
      type: Boolean,
      default: false
    },
    forceShowDate: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showContact: {
      type: Boolean,
      default: true
    },
    showStatus: {
      type: Boolean,
      default: false
    },
    showStatusColors: {
      type: Boolean,
      default: false
    },
    showWorker: {
      type: Boolean,
      default: true
    },
    reservationData: {
      type: Object
    },
    reservationHoursFirst: {
      type: Boolean,
      default: false
    },
    useInProgress: {
      type: Boolean,
      default: false
    },
    blockChangeActions: {
      type: Boolean,
      default: false
    },
    indexOnList: {
      type: Number,
      default: 0
    },
    daysMode: {
      type: Boolean,
      default: false
    },
    isTabbedView: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'avatar': Avatar,
    'periodicity-viewer': PeriodicityViewer,
    'reservation-status': ReservationStatus,
    'tooltip': Tooltip
  },
  data () {
    return {
      customerIsPresent: -1,
      acceptInProgress: false,
      rejectInProgress: false,
      presentInProgress: false,
      unblockInProgress: false
    };
  },
  computed: {
    cssClasses () {
      let baseClasses = {
        'reservation': true,
        'is-blocked-date': this.isBlockedDate,
        'is-row': !this.isBlock,
        'in-calendar': this.inCalendar,
        'show-worker': this.displayWorker,
        'has-hours-first': this.reservationHoursFirst
      };

      if (this.showStatusColors) {
        baseClasses = Object.assign(baseClasses, {
          'is-accepted': this.reservationData.status === 3,
          'is-rejected': this.reservationData.status === 4 || this.reservationData.status === 5,
          'is-waiting-list': this.reservationData.status === 6
        });
      }

      return baseClasses;
    },
    displayWorker () {
      return this.workersEnabled && (!this.inCalendar || (this.inCalendar && this.showWorker)) && this.reservationData.worker;
    },
    date () {
      let dateFormat = this.$t('reservation.dateFormat');
      let reservationDate = this.reservationData.date;
      reservationDate = reservationDate.split('-');
      reservationDate[1] = parseInt(reservationDate[1], 10) - 1;
      let newTimestamp = new Date(reservationDate[0], reservationDate[1], reservationDate[2]);

      return this.$moment(newTimestamp).format(dateFormat);
    },
    startTime () {
      let startTime;

      if (this.inCalendar) {
        startTime = this.reservationData.time_from;
      } else {
        startTime = this.reservationData.start_at.split(' ')[1];
      }

      startTime = startTime.split(':');
      return [startTime[0], startTime[1]];
    },
    endTime () {
      let endTime;

      if (this.inCalendar) {
        endTime = this.reservationData.time_to;
      } else {
        endTime = this.reservationData.end_at.split(' ')[1];
      }

      endTime = endTime.split(':');
      return [endTime[0], endTime[1]];
    },
    duration () {
      return this.reservationData.duration + 'm';
    },
    clientName () {
      let clientName;

      if (this.inCalendar) {
        clientName = this.reservationData.person.name;
      } else {
        clientName = this.reservationData.user_name;
      }

      return clientName;
    },
    peopleNumber () {
      return this.reservationData.people_number;
    },
    childrenNumber () {
      return this.reservationData.children_number;
    },
    phone () {
      if (this.inCalendar) {
        if (this.reservationData.person.phone !== '') {
          return this.reservationData.person.phone;
        }
      } else {
        if (this.reservationData.phone !== '') {
          return this.reservationData.phone;
        }
      }

      return false;
    },
    email () {
      if (this.inCalendar) {
        if (this.reservationData.person.email !== '') {
          return this.reservationData.person.email;
        }
      } else {
        if (this.reservationData.confirm_email !== '') {
          return this.reservationData.confirm_email;
        }
      }

      return false;
    },
    occupancyPeople () {
      if (this.reservationData.other_inquiries !== null) {
        return this.reservationData.other_inquiries.people_number;
      }

      return 0;
    },
    occupancyReservations () {
      if (this.reservationData.other_inquiries !== null) {
        return this.reservationData.other_inquiries.sum;
      }

      return 0;
    },
    serviceName () {
      if (this.reservationData.service !== null) {
        return this.reservationData.service.name;
      }

      return false;
    },
    hasPrice () {
      return !this.isBlockedDate && this.reservationData.payment !== null;
    },
    price () {
      if (this.reservationData.payment !== null) {
        let priceParts = this.reservationData.payment.price.split('.');

        if (priceParts[1] === '00') {
          return priceParts[0];
        }

        return this.reservationData.payment.price;
      }

      return false;
    },
    priceCurrency () {
      if (this.reservationData.payment && this.reservationData.payment.currency) {
        return this.reservationData.payment.currency;
      } else if (this.reservationData.configuration_payment) {
        return this.reservationData.configuration_payment.currency;
      }

      return this.$root.defaultCurrency;
    },
    totalPrice () {
      return CurrencyUtils.getQuote(this.price, this.priceCurrency);
    },
    paymentStatus () {
      if (this.reservationData.payment.status === 'COMPLETED') {
        return this.$t('reservation.paid');
      }

      if (this.reservationData.payment.status === 'CASH') {
        return this.$t('common.ui.paidWithCash');
      }

      if (this.reservationData.payment.status === 'CARD_REGISTERED') {
        return this.$t('reservation.cardRegistered');
      }

      if (this.reservationData.payment.status === 'ADVANCED') {
        return this.$t('payments.statusAdvanced');
      }

      return this.$t('reservation.unpaid');
    },
    paymentStatusClass () {
      if (['CASH', 'COMPLETED', 'CARD_REGISTERED'].indexOf(this.reservationData.payment.status) > -1) {
        return 'is-paid';
      }

      if (this.reservationData.payment.status === 'ADVANCED') {
        return 'is-partially-paid';
      }

      return 'is-not-paid';
    },
    serviceStyles () {
      if (this.servicesEnabled && this.reservationData.service && this.reservationData.service.color) {
        if (this.isBlock) {
          return 'border-bottom: 3px solid ' + this.reservationData.service.color;
        } else {
          return 'border-left: 3px solid ' + this.reservationData.service.color;
        }
      }

      return '';
    },
    inProgress () {
      let currentTime = new Date();
      let currentHour = currentTime.getHours();
      let currentMinutes = currentTime.getMinutes();
      let startTime = this.reservationData.time.split(':').map(n => parseInt(n, 10));
      let startDate = this.reservationData.date.split('-').map(n => parseInt(n, 10));
      let endTime = this.reservationData.end_at.split(' ')[1].split(':').map(n => parseInt(n, 10));
      let currentMinutesStamp = currentHour * 60 + currentMinutes;
      let startMinutesStamp = startTime[0] * 60 + startTime[1];
      let endMinutesStamp = endTime[0] * 60 + endTime[1];

      if (
        startDate[0] === currentTime.getFullYear() &&
        startDate[1] - 1 === currentTime.getMonth() &&
        startDate[2] === currentTime.getDate()
      ) {
        return startMinutesStamp <= currentMinutesStamp && endMinutesStamp >= currentMinutesStamp;
      }

      return false;
    },
    isBlockedDate () {
      return ['blocking', 'cart', 'company'].indexOf(this.reservationData.type) > -1;
    },
    isCyclic () {
      return this.reservationData.has_periodicity;
    },
    reservationDateStart () {
      if (this.reservationData.start_at) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.start_at) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.start_at, false);
      }

      return '-';
    },
    reservationDateEnd () {
      if (this.reservationData.end_at) {
        return CalendarUtils.getDayNameFromDate(this.reservationData.end_at) + ', ' + CalendarUtils.humanDateFormat(this.reservationData.end_at, false);
      }

      return '-';
    },
    calendarUtils () {
      return CalendarUtils;
    }
  },
  methods: {
    clientNotPresent () {
      this.$bus.$emit('confirm-display', {
        title: this.$t('reservation.clientNotPresentTitle'),
        desc: this.$t('reservation.clientNotPresentDesc'),
        cancelLabel: this.$t('common.ui.cancel'),
        cancelClick: () => false,
        okLabel: this.$t('reservation.clientNotPresentBtn'),
        okClick: this.setPresentState,
        isDanger: true
      });
    },
    setPresentState () {
      this.presentInProgress = true;

      this.$http.post('/panel/attendances/status/' + this.reservationData.id + '/2').then(response => {
        this.presentInProgress = false;
        this.$store.commit('global/removeInquiry', this.reservationData.id);
        this.$bus.$emit('notification-display', {
          type: 'success',
          message: this.$t('reservation.clientNotPresentSaved')
        });
        this.$bus.$emit('dashboard-reservations-list-reload-needed');
        this.$bus.$emit('calendar-layout-reload-needed');
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/attendances/status/' + this.reservationData.id + '/2', this.$router, this.$bus, this.setPresentState.bind(this));
        this.presentInProgress = false;
      });
    },
    showReservationDetails () {
      let type = this.isBlockedDate ? 'blocking' : '';
      this.$bus.$emit('show-reservation-details-popup', this.reservationData.id, type);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.reservation {
  background: $color-light;
  border: 1px solid $color-border;
  border-radius: $border-radius-small;
  margin: 4rem 0 1rem;
  max-width: 25rem;
  padding: 2rem 1rem 6.75rem 1rem;
  position: relative;
  text-align: center;
  width: 100%;

  &.is-blocked-date {
    background: $color-light;
    border-style: dashed;
    border-width: 1px;
  }

  &.in-calendar {
    margin: 1rem 0;
  }

  &.is-accepted {
    background: lighten($color-success, 51%);
  }

  &.is-rejected {
    background: lighten($color-danger, 45%);
  }

  &.is-waiting-list {
    background: lighten($color-warning, 40%);
  }

  &:first-child {
    margin-left: 0;
  }

  .avatar {
    left: 50%;
    position: absolute;
    top: 0;
    transform: translateX(-50%) translateY(-50%);
    z-index: 1;

    &.is-blocked {
      left: calc(50% + 20px);

      .icon {
        background: $color-bg-light;
        border: 1px solid $color-border;
        border-radius: 50%;
        height: 32px;
        left: -56px;
        padding: 8px;
        position: absolute;
        top: -16px;
        width: 32px;
      }
    }
  }

  .time-data {
    font-size: 2.4rem;
    margin-top: 1.5rem;

    &:first-child {
      margin-top: 0;
    }

    .date {
      color: $color-text-light;
      display: block;
      font-size: 1.3rem;
      margin-bottom: .5rem;
      width: 100%;
    }

    .time {
      width: 100%;
    }
  }

  &.is-row .time-data {
    margin-top: 0;
  }

  &.is-row.has-hours-first {
    & > .time {
      width: 10rem;
    }

    & > .avatar {
      left: 11rem;
    }
  }

  .duration,
  .duration-range {
    color: $color-text-light;
    font-size: 1.4rem;
    font-weight: 600;

    svg {
      position: relative;
      top: .1rem;
    }
  }

  .duration-range {
    margin-top: -1.5rem;
  }

  .contact-data {
    padding: .5rem 0;

    .client-name {
      display: block;
      font-size: 1.3rem;
      margin-bottom: .5rem;
      padding: .5rem 0;
    }

    .client-first-visit {
      display: block;
      margin: -.5rem auto .5rem auto;
      width: fit-content;
    }

    .people-number {
      &-counter {
        background: $color-text-light;
        border: 2px solid $color-light;
        border-radius: 1rem;
        color: $color-light;
        font-size: 1.2rem;
        font-weight: bold;
        margin-left: -.7rem;
        padding: .125rem .5rem;
        position: relative;
        top: -.4rem;
      }
    }

    .client-info {
      cursor: pointer;
    }

    .email,
    .phone,
    .client-info {
      padding: 0 .25rem;
      position: relative;

      &:hover {
        .tooltip {
          opacity: 1;
          top: -3rem;
        }
      }
    }

    .tooltip {
      background: $color-dark;
      border-radius: $border-radius-small;
      color: $color-light;
      display: block;
      font-size: 1.4rem;
      font-weight: 400;
      left: 50%;
      opacity: 0;
      padding: .5rem 1rem;
      pointer-events: none;
      position: absolute;
      top: -2.5rem;
      transform: translateX(-50%);
      transition: all .25s ease-out;
      white-space: nowrap;
      z-index: 1;

      &:after {
        border: .5rem solid $color-dark;
        border-left-color: transparent;
        border-right-color: transparent;
        border-bottom-color: transparent;
        bottom: -1rem;
        content: "";
        height: 1rem;
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        width: 1rem;
      }
    }
  }

  .occupancy {
    &-date {
      color: $color-text-light;
      font-size: 1.1rem;
    }

    &-time {
      background: $color-notice;
      border-radius: .9rem;
      color: $color-light;
      display: inline-block;
      font-size: 1.2rem;
      font-weight: 600;
      letter-spacing: -.5px;
      line-height: 1.8rem;
      margin: 1rem auto 0 auto;
      padding: .25rem .5rem;
      width: 16rem;
    }
  }

  .price {
    border-top: 1px solid $color-border;
    font-size: 1.3rem;
    margin: .5rem 0 0 0;

    .payment-status {
      .is-paid {
        color: $color-success;
      }

      .is-partially-paid {
        color: $color-link;
      }

      .is-not-paid {
        color: $color-danger;
      }
    }

    div.payment-status {
      width: 100%;
    }

    .payment-status,
    .total-price,
    .periodicity-name {
      color: $color-text-light;
      display: inline-block;
      font-size: 1.4rem;
      font-weight: normal;
      letter-spacing: -.025rem;
      margin: 1.5rem 0 0 0;
    }

    .periodicity-name {
      margin: 0 0 .5rem 0;
    }

    .service {
      cursor: pointer;
      display: block;
      font-size: 1.3rem;
      font-weight: bold;
      padding: .75rem 0;
      position: relative;

      & ~ .payment-status,
      & ~ .total-price {
        font-size: 1.3rem;
        margin-top: 0;
      }

      &.no-price {
        margin-top: 1rem;
      }

      &:hover {
        .tooltip {
          opacity: 1;
          top: 50%;

          &.tooltip-top {
            top: -24px;
          }
        }
      }
    }
  }

  .actions {
    align-items: center;
    bottom: 2rem;
    display: flex;
    justify-content: center;
    left: 50%;
    margin: .5rem 0 0 0;
    padding: 1.5rem 0 0 0;
    position: absolute;
    transform: translateX(-50%);

    .button {
      margin: 0 .5rem;

      &:first-of-type {
        margin-left: 0;
      }
    }
  }

  &.is-row {
    align-items: center;
    border-radius: 0;
    display: flex;
    margin-bottom: -.1rem!important;
    margin-top: 0!important;
    max-width: 100%!important;
    padding: 1rem 3rem;
    text-align: left;
    width: 100%!important;

    &:last-child {
      margin-bottom: 0!important;
    }

    .avatar {
      left: 1.5rem;
      top: 50%;
      transform: translateY(-50%);
    }

    .time {
      position: relative;
      width: auto;

      &.in-progress:after {
        background: $color-warning;
        border-radius: $border-radius-small;
        color: $color-light;
        content: attr(data-lang-in-progress);
        font-size: 1.2rem;
        font-weight: bold;
        left: 50%;
        padding: 1px 5px;
        position: absolute;
        text-align: center;
        text-transform: uppercase;
        top: -22px;
        transform: translateX(-50%);
        width: 100%;
      }
    }

    .contact-data {
      order: 1;
      padding-left: 6.5rem;
      width: 25%;

      &.no-worker {
        padding-left: 0;
      }

      .client-first-visit {
        margin: 0 0 .5rem 0;
      }
    }

    .time-data {
      display: flex;
      flex-wrap: wrap;
      font-size: 1.8rem;
      order: 2;
      padding-left: 2rem;
      width: 40%;

      .duration,
      .duration-range {
        margin-left: 1rem;
        position: relative;
        top: 2px;

        &:first-child {
          margin-left: 0;
        }
      }

      .occupancy {
        display: block;
        width: 100%;

        & > .occupancy-time {
          display: inline-block;
          margin: .5rem 1rem 0 0;
          padding: 0 .75rem;
          text-align: center;
          width: auto;
        }
      }
    }

    .price {
      border-top: none;
      margin-top: 0;
      order: 3;
      width: 25%;

      .service,
      .service.no-price {
        margin-top: 0;
      }

      .total-price {
        margin: 0;
      }

      .payment-status {
        margin-top: 0;
      }
    }

    .actions {
      align-items: center;
      display: flex;
      justify-content: center;
      margin-left: auto;
      margin-top: 0;
      order: 4;
      padding-top: 0;
      position: static;
      text-align: right;
      transform: none;

      .button {
        margin: 0 .5rem;
        text-align: center;
      }
    }
  }

  .reservation-id {
    display: block;
    margin-bottom: .5rem;
    width: 100%;
  }
}

@media (max-width: 1240px) {
  .reservation {
    &.in-calendar {
      padding-bottom: 7rem;

      .actions {
        flex-wrap: wrap;
        width: 100%;

        .button {
          margin: 5rem 1% 0 1%;
          width: 20%;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/Reservation.vue